import React from 'react';
import {Link, useHistory} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import styled, {css} from 'styled-components';
import {useSelector} from 'react-redux';
import Lottie from 'react-lottie';

import BigButton from './../BigButton';

import {
    breakpoints,
    Flex,
    mobileMediaQuery,
    notWebMediaQuery,
    tabletMediaQuery,
    useBreakpoint,
} from './../../styles';
import {debugCNitoUrl} from '../../helpers';

import CNitoAvatars from '../../assets/avatars/cnito';
import CartoonitoLogo from '../../assets/cnito_logo_mixed.png';
import borderTop from '../../assets/cornered-shape-yellow.png';
import {ReactComponent as HomeIcon} from './../../assets/home_icon.svg';
import ShowsIcon from './../../assets/shows_icon.png';
import ShowsIconSelected from './../../assets/shows_icon_selected.png';
import {ReactComponent as GamesIcon} from './../../assets/games_icon.svg';
import avatarIcon from '../../assets/avatars/cnito/avatar-1.png';
import {ReactComponent as Chevron} from './../../assets/arrow.svg';
import {ReactComponent as Close} from './../../assets/close.svg';

const CartoonitoHeader = ({pathname}) => {
    const {t} = useTranslation();
    const user = useSelector(state => state.user);
    const userAvatar = user.avatar;
    const showBackButton = pathname.indexOf('/cartoon/') >= 0;
    const history = useHistory();
    const breakpoint = useBreakpoint();

    if (pathname.indexOf('/play') === 0) {
        return (
            <Flex
                position="fixed"
                width="100%"
                padding="34px 53px"
                justifyContent="space-between"
                background="transparent"
            >
                <Flex margin="auto 0 auto 0">
                    <BackButton onClick={() => history.goBack()}>
                        <CloseIcon light title={t('controls.goback')} />
                        {t('controls.goback')}
                    </BackButton>
                </Flex>
            </Flex>
        );
    }

    return (
        <Container fixed={['xs', 'mobile'].includes(breakpoint)}>
            <Flex margin="auto 0 auto 0">
                {showBackButton ? (
                    <BackButton onClick={() => history.goBack()}>
                        <ChevronIcon title={t('controls.goback')} />
                        {t('controls.goback')}
                    </BackButton>
                ) : (
                    <Logo />
                )}
            </Flex>

            <ButtonsRow>
                <BigButton
                    as={Link}
                    active={['/', '/home'].includes(pathname) ? 1 : 0}
                    children={<HomeIcon title={t('home.title')} />}
                    to={`/home${debugCNitoUrl()}`}
                    value={t('home.title')}
                    iconWidth="28px"
                    iconHeight="28px"
                    title={t('home.title')}
                    transparent
                    dark
                    cnito
                />
                <BigButton
                    as={Link}
                    active={pathname.indexOf('/cartoons') >= 0 ? 1 : 0}
                    icon={
                        pathname.indexOf('/cartoons') >= 0
                            ? ShowsIconSelected
                            : ShowsIcon
                    }
                    to={`/cartoons${debugCNitoUrl()}`}
                    value={t('cartoonito.cartoons.title')}
                    iconWidth="28x"
                    iconHeight="28px"
                    title={t('cartoonito.cartoons.title')}
                    transparent
                    dark
                    cnito
                />
                {false && <BigButton
                    as={Link}
                    active={pathname.indexOf('/games') >= 0 ? 1 : 0}
                    children={<GamesIcon title={t('cartoonito.games.title')} />}
                    to={`/games${debugCNitoUrl()}`}
                    value={t('cartoonito.games.title')}
                    iconWidth="28x"
                    iconHeight="28px"
                    title={t('cartoonito.games.title')}
                    transparent
                    dark
                    cnito
                />}
            </ButtonsRow>

            <BigButton
                as={Link}
                to={`/onboarding${debugCNitoUrl()}`}
                title={t('controls.profile')}
                minWidth="fit-content"
                flex="0"
                margin="0"
                iconWidth="32px"
                iconHeight="32px"
                padding="0"
                children={
                    userAvatar === null ? (
                        <ProfileIcon />
                    ) : (
                        <AvatarIcon>
                            <Lottie
                                options={{
                                    loop: false,
                                    autoplay: true,
                                    animationData: CNitoAvatars[userAvatar][0],
                                    isClickToPauseDisabled: true,
                                    isStopped: false,
                                    isPaused: false,
                                }}
                            />
                        </AvatarIcon>
                    )
                }
                transparent
            />
        </Container>
    );
};

const Container = styled(Flex)`
    position: absolute;
    z-index: 100;
    top: 0;
    box-sizing: border-box;
    transition: background-color 0.3s ease;
    height: 100px;
    padding-left: 40px;
    padding-right: 40px;
    flex-wrap: nowrap;
    background-color: #f6d703;
    width: 100%;
    padding: 15px;
    justify-content: space-between;

    ${mobileMediaQuery} {
        height: 100px;
    }

    ${({fixed}) =>
        fixed &&
        css`
            position: relative;
        `}

    ::before {
        content: '';
        position: absolute;
        bottom: -21px;
        left: 0px;
        right: 0px;
        width: 100%;
        background-image: url(${borderTop});
        background-repeat: repeat-x;
        height: 22px;
        z-index: 100;
    }
`;

/* eslint-disable max-len */
const Logo = styled.img.attrs(() => {
    return {
        src: CartoonitoLogo,
        alt: 'Cartoonito Logo',
    };
})`
    width: 235px;
    height: 52px;

    @media screen and (max-width: ${breakpoints.sm}px) {
        width: 181px;
        height: 40px;
    }

    @media screen and (max-width: 502px) {
        width: 130px;
        height: auto;
    }

    @media screen and (max-width: 450px) {
        width: 100px;
        height: auto;
    }
`;

const ButtonsRow = styled.nav`
    height: 100%;
    width: auto;
    display: flex;
    justify-content: center;
    align-items: center;
    margin: auto;

    a {
        flex: 1;
        max-width: 78px;
        :not(:last-of-type) {
            margin-right: 15px;
        }

        @media screen and (max-width: 450px) {
            max-width: min-content;

            & > svg {
                max-width: 24px;
            }
            & > span {
                font-size: 12px;
            }
        }
    }

    ${tabletMediaQuery} {
        margin-right: 140px;
    }

    @media screen and (min-width: ${breakpoints.mobile}px) and (max-width: ${breakpoints.sm}px) {
        margin-right: 94px;
    }

    ${mobileMediaQuery} {
        margin-right: 94px;
    }

    ${notWebMediaQuery} {
        margin-left: auto;
        margin-right: auto;
    }
`;

const ProfileIcon = styled.img.attrs(() => {
    return {
        src: avatarIcon,
    };
})`
    width: 32px;
    height: 32px;
    filter: drop-shadow(1px 1px 0 #000) drop-shadow(0px 1px 0 #000)
        drop-shadow(-1px 0px 0 #000) drop-shadow(1px -1px 0 #000);
`;

const BackButton = styled(Flex)`
    color: rgb(0, 0, 0);
    font-size: 22px;
    font-family: 'Rodger';
    font-weight: bold;
    letter-spacing: 1.22px;
`;

const ChevronIcon = styled(Chevron)`
    margin-right: 14px;
    width: 12px;
    height: 22px;
    transform: scaleX(-1);
    path {
        fill: ${({light}) => (light ? '#fff' : '#000')};
    }
`;

const CloseIcon = styled(Close)`
    margin-right: 14px;
    width: 28px;
    height: 28px;

    g {
        fill: ${({light}) => (light ? '#fff' : '#000')};
    }

    ${notWebMediaQuery} {
        width: 20px;
        height: 20px;
    }
`;

const AvatarIcon = styled.div`
    width: 80px;
    height: 100%;
    svg {
        transform: scale(2) translateY(-12px) !important;
    }
`;

export default CartoonitoHeader;
